import { useId, useState } from "react";
import { IconButton, Divider, Menu, MenuItem } from "@mui/material";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TextEnum } from "../../SolarGikLib/TextStyles";
import type { RootState } from "../app/Store";
import type { SiteGroup } from "../site_groups/SiteGroupsApi";
import SiteNameComponent from "../siteName/SiteNameComponent";
import { getSiteGroupNavigation } from "./SiteGroupNavigation";
import classes from "./SiteGroupSwitcher.module.css";
import { siteGroupPath } from "../site_groups/SiteGroupRoutes";

export default function SiteGroupSwitcher({
  groups,
  siteId,
  dashboardGroupId,
}: {
  groups: SiteGroup[];
  siteId: string;
  dashboardGroupId?: number;
}) {
  const navigate = useNavigate();
  const metadata = useSelector((state: RootState) => state.multiSitesMetadata.sites);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const visibleGroups = getSiteGroupNavigation(
    groups,
    siteId,
    dashboardGroupId,
    (id) => metadata[id]?.displayName || metadata[id]?.siteCode || id
  );
  if (visibleGroups.length === 0) {
    return null;
  }
  const go = (path: string, siteGroupId?: number) => {
    setAnchor(null);
    navigate(path, { state: siteGroupId === undefined ? null : { siteGroupId } });
  };
  const groupItem = (group: SiteGroup) => (
    <MenuItem
      key={group.id}
      selected={group.id === dashboardGroupId}
      disabled={group.id === dashboardGroupId}
      onClick={() => go(siteGroupPath(group))}
      className={TextEnum.h5}
      sx={{ fontWeight: 700, whiteSpace: "normal" }}
    >
      {group.name}
    </MenuItem>
  );
  return (
    <>
      <IconButton
        className={`${classes.button} ${TextEnum.h4}`}
        title="Group"
        aria-label="Switch within site groups"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        aria-controls={anchor ? menuId : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        <span className={classes.groupName}>Group</span>
        <KeyboardArrowDown sx={{ flexShrink: 0 }} />
      </IconButton>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        MenuListProps={{ "aria-label": "Site group navigation" }}
        sx={{
          "& .MuiPaper-root": { borderRadius: "12px", maxWidth: "min(420px, calc(100vw - 32px))" },
          "& .MuiMenuItem-root": { fontFamily: "var(--font-family)" },
        }}
      >
        {visibleGroups.flatMap((group, index) => [
          ...(index > 0 ? [<Divider key={`divider-${group.id}`} />] : []),
          groupItem(group),
          ...group.siteIds.map((memberId) => {
            const code = metadata[memberId]?.siteCode;
            return (
              <MenuItem
                key={`${group.id}:${memberId}`}
                selected={!dashboardGroupId && siteId === memberId}
                disabled={!code || (!dashboardGroupId && siteId === memberId)}
                className={TextEnum.h5}
                sx={{ paddingLeft: 4, whiteSpace: "normal" }}
                onClick={() => go(`/${encodeURIComponent(code!)}/Home`, group.id)}
              >
                {code ? <SiteNameComponent siteId={memberId} /> : memberId}
              </MenuItem>
            );
          }),
        ])}
      </Menu>
    </>
  );
}
